import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import PropTypes from "prop-types";
import React, { useMemo, useState } from "react";
import { NavLink } from "react-router-dom";

import {
  Link,
  NotificationType,
  TextTooltip,
} from "@transfr-inc/dashboard-components";
import { useContainerQuery } from "@transfr-inc/dashboard-components/container-query";
import {
  Column,
  ColumnOptions,
  ColumnType,
  DataTable,
  SortDirection,
} from "@transfr-inc/dashboard-components/data-table";

import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownItems,
} from "@transfr-inc/dashboard-components/forms";
import IntegrationDate from "./integration-date";
import IntegrationDateWithBadge from "./integration-date-with-badge";
import "./integration-table.scss";

import {
  RemoveIntegrationModal,
  SyncIntegrationModal,
} from "@components/modals";
import { useContainer } from "@src/contexts/container-context";
import { useCurrentUser } from "@queries";
import { useProviderIcons } from "@queries/provider-icon";

/**
 * Dropdown menu option component for integration actions.
 * Used in the actions column dropdown menu.
 */
const DropdownOption = ({ data, icon, text, helpText }) => (
  <DropdownItem value={{ action: text, data }}>
    <div className="dropdown-item">
      <FontAwesomeIcon icon={icon} />
      <div className="option-label">{text}</div>
      {!!helpText && (
        <TextTooltip text={helpText} position="top-end">
          <FontAwesomeIcon icon="fa-regular fa-circle-info" />
        </TextTooltip>
      )}
    </div>
  </DropdownItem>
);

/**
 * Table component for displaying integration connections.
 *
 * This component renders a data table with columns for:
 * - Organization (if multi-org context)
 * - Provider name with icon and link to sync history
 * - Integrated date (for dynamic connections)
 * - Last sync date with status badge
 * - Actions dropdown (for dynamic connections)
 *
 * The component handles:
 * - Adding provider icons to connection data
 * - Opening modals for sync/remove actions
 *
 * @param {Array} connectionList - Array of connection objects to display
 * @param {Function} launchNotification - Function to show notifications
 * @param {boolean} isLoading - Whether a sync operation is in progress
 * @param {Function} refresh - Function to refresh connection data
 * @param {boolean} isStaticConnection - Whether connections are static (read-only)
 */
const IntegrationTable = ({
  connectionList,
  launchNotification,
  isLoading,
  refresh,
  isStaticConnection = false,
}) => {
  const currentUser = useCurrentUser();
  const { id: userId } = currentUser || {};
  const { integrationService } = useContainer();

  const providerIds = useMemo(
    () => connectionList.map((c) => c.providerId).filter(Boolean),
    [connectionList]
  );
  const { data: icons = {} } = useProviderIcons(providerIds, {
    enabled: providerIds.length > 0,
  });
  const [syncModalData, setSyncModalData] = useState();
  const [removeModalData, setRemoveModalData] = useState();

  const responsiveQueryForTable = {
    "small-panel": {
      maxWidth: 430,
    },
    "large-panel": {
      minWidth: 430,
    },
  };

  const [responsiveStylesForTable, containerRef] = useContainerQuery(
    responsiveQueryForTable
  );

  // Add provider icons to connection data for display
  const connectionsWithIcons = useMemo(
    () =>
      connectionList.map((c) => ({
        ...c,
        icon: icons[c.providerId],
      })),
    [connectionList, icons]
  );

  // Determine if organization column should be shown (multi-org context)
  const showOrgColumn = useMemo(
    () => connectionsWithIcons.some((r) => !!r.organizationName),
    [connectionsWithIcons]
  );

  const onOptionSelected = ({ action, data }) => {
    switch (action) {
      case "remove":
        setRemoveModalData(data);
        break;
      case "sync":
        setSyncModalData(data);
        break;
      default:
        console.error("Invalid action selected:", action);
        break;
    }
  };

  const handleCloseModal = () => {
    setSyncModalData();
    setRemoveModalData();
  };

  const syncIntegration = async (data) => {
    try {
      await integrationService.syncIntegration(data.id, userId);
      await refresh(data.lastRunDate, data.id);
    } catch {
      launchNotification({
        type: NotificationType.error,
        message: `An error occurred and your ${
          data.providerName || ""
        } integration did not sync properly. Please try again.`,
        icon: ["fa-solid", "triangle-exclamation"],
      });
    }
    handleCloseModal();
  };

  const onRemoveIntegration = async (data) => {
    try {
      await integrationService.removeIntegration(data.id);
      launchNotification({
        type: NotificationType.success,
        message: `Your ${
          data.providerName || ""
        } integration has been removed.`,
        icon: ["fa-solid", "circle-check"],
      });
      refresh();
    } catch {
      launchNotification({
        type: NotificationType.error,
        message: `An error ocurred during integration removal. Please try again.`,
        icon: ["fa-solid", "triangle-exclamation"],
      });
    }
    handleCloseModal();
  };

  // Build table columns based on connection type and context
  const columns = useMemo(() => {
    const cols = [];

    // Organization column (only shown in multi-org context)
    if (showOrgColumn) {
      cols.push(
        new Column(
          "organizationName",
          "Organization",
          ColumnType.BASIC,
          new ColumnOptions({ sortingEnabled: true })
        )
      );
    }

    // Provider name column with icon and link to sync history
    cols.push(
      new Column(
        "providerName",
        "Name",
        ColumnType.CUSTOM,
        new ColumnOptions({
          sortingEnabled: true,
          customFunction: (value, rowData) => (
            <div className="name-block">
              <div className="image-block">
                <img
                  className="provider-image"
                  src={icons[rowData.providerId]}
                  alt={value}
                />
              </div>
              <div className="link-block">
                <Link
                  routerComponent={NavLink}
                  underline
                  to={`/app/integrations/${
                    rowData.organizationCode || currentUser?.organizationCode
                  }/${rowData.id}/sync-history`}
                >
                  View Event Log
                </Link>
              </div>
            </div>
          ),
        })
      )
    );

    // Integrated date column (only for dynamic connections)
    if (!isStaticConnection) {
      cols.push(
        new Column(
          "createdDate",
          "Integrated",
          ColumnType.CUSTOM,
          new ColumnOptions({
            sortingEnabled: true,
            customFunction: (value) => <IntegrationDate date={value} />,
          })
        )
      );
    }

    // Last sync column with status badge
    cols.push(
      new Column(
        "lastRunDate",
        "Last Sync",
        ColumnType.CUSTOM,
        new ColumnOptions({
          sortingEnabled: true,
          customFunction: (value, rowData) =>
            value ? (
              <IntegrationDateWithBadge connection={rowData} />
            ) : (
              "Pending"
            ),
        })
      )
    );

    // Actions column with dropdown menu (only for dynamic connections)
    if (!isStaticConnection) {
      cols.push(
        new Column(
          "id",
          " ",
          ColumnType.CUSTOM,
          new ColumnOptions({
            customFunction: (id, rowData) => (
              <Dropdown
                className={clsx("integration-menu", "icon-only")}
                selected={{}}
                onOptionSelected={onOptionSelected}
                disabled={isLoading}
              >
                <DropdownButton
                  iconOnly
                  icon={["fa-regular", "ellipsis-v"]}
                  className="borderless"
                />
                <DropdownItems>
                  <DropdownOption
                    data={rowData}
                    icon={["fa-regular", "times"]}
                    text="remove"
                  />
                  <DropdownOption
                    data={rowData}
                    icon={["fa-regular", "rotate"]}
                    text="sync"
                    helpText="Sync will happen automatically every 24 hours. You can also manually sync your classroom data now."
                  />
                </DropdownItems>
              </Dropdown>
            ),
          })
        )
      );
    }

    return cols;
  }, [
    showOrgColumn,
    icons,
    currentUser?.organizationCode,
    isStaticConnection,
    isLoading,
    onOptionSelected,
  ]);

  return (
    <div className={clsx("integration-table-container")} ref={containerRef}>
      <DataTable
        className={clsx("connections-table", responsiveStylesForTable, {
          "is-static-connection": isStaticConnection,
          "with-organization": showOrgColumn,
        })}
        columns={columns}
        data={connectionsWithIcons}
        keyFields={["id"]}
        defaultSortPropName={
          isStaticConnection ? "organizationName" : "createdDate"
        }
        defaultSortDirection={SortDirection.ASCENDING}
        responsiveStyles={responsiveStylesForTable}
        rowHeightConfig={{ large: 54, medium: 160, small: 280 }}
      />
      {!!syncModalData && (
        <SyncIntegrationModal
          integration={syncModalData}
          open={!!syncModalData}
          onCancel={handleCloseModal}
          onSyncIntegration={syncIntegration}
        />
      )}
      {!!removeModalData && (
        <RemoveIntegrationModal
          integration={removeModalData}
          open={!!removeModalData}
          onCancel={handleCloseModal}
          onRemoveIntegration={onRemoveIntegration}
        />
      )}
    </div>
  );
};

IntegrationTable.propTypes = {
  connectionList: PropTypes.array.isRequired,
  launchNotification: PropTypes.func.isRequired,
  refresh: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isStaticConnection: PropTypes.bool,
};

export default IntegrationTable;
